import { QueryClient, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";
import { projectPath as productionPath } from "../api/studio";
import type {
  Bootstrap,
  CastAsset,
  Job,
  Production,
  ProductionKind,
  ProductionSummary,
} from "../api/types";

export function isActiveJob(job: Job): boolean {
  return [
    "pending",
    "queued",
    "running",
    "processing",
    "stopping",
    "cancelling",
  ].includes(job.status);
}
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) =>
        count < 1 &&
        (!(error instanceof ApiError) ||
          error.status === 0 ||
          error.status >= 500),
    },
    mutations: { retry: false },
  },
});
export const productionKey = (kind: ProductionKind, id: string) =>
  ["production", kind, id] as const;
export function productionPollInterval(project?: Production): number | false {
  return project?.jobs.some(isActiveJob) ? 2500 : false;
}
export function useBootstrap() {
  return useQuery({
    queryKey: ["bootstrap"],
    queryFn: ({ signal }) => api<Bootstrap>("/api/bootstrap", { signal }),
  });
}
export function useProductions(kind: ProductionKind) {
  return useQuery({
    queryKey: ["productions", kind],
    queryFn: ({ signal }) =>
      api<ProductionSummary[]>(productionPath(kind), { signal }),
  });
}
export function useProduction(kind: ProductionKind, id: string) {
  return useQuery({
    queryKey: productionKey(kind, id),
    queryFn: ({ signal }) =>
      api<Production>(productionPath(kind, id), { signal }),
    refetchInterval: (query) => productionPollInterval(query.state.data),
    refetchIntervalInBackground: false,
  });
}
export function useCastAssets() {
  return useQuery({
    queryKey: ["cast-assets"],
    queryFn: ({ signal }) => api<CastAsset[]>("/api/cast-assets", { signal }),
  });
}
